import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Wallet, Bank, CurrencyDollar, QrCode, Globe, Money, Check,
  CheckCircle, Copy, ChatCircleText, ShieldCheck,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  bank: Bank, upi: Wallet, paypal: CurrencyDollar, stripe_connect: CurrencyDollar,
  razorpay: CurrencyDollar, venmo: CurrencyDollar, mpesa: Wallet, qr: QrCode,
  crypto: Globe, cash: Money,
};

const TYPE_INSTRUCTIONS = {
  cash: 'Pay in cash when you arrive — the seller will mark your booking as paid.',
  bank: 'Transfer the total to the seller\u2019s bank account using the details below, then return here and mark as paid.',
  upi: 'Pay via any UPI app (GPay, PhonePe, Paytm, ...) to the seller\u2019s UPI ID below.',
  paypal: 'Pay via PayPal using the seller\u2019s details below.',
  venmo: 'Send the total via Venmo to the seller\u2019s username below.',
  mpesa: 'Send the total via M-Pesa to the seller\u2019s number below.',
  qr: 'Scan the QR code with your favourite payment app, then return here and mark as paid.',
  crypto: 'Send the total to the seller\u2019s crypto wallet address below.',
  stripe_connect: 'You\u2019ll be redirected to a secure Stripe checkout to pay.',
  razorpay: 'You\u2019ll be redirected to Razorpay to pay.',
};

const BookingPaymentPicker = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [booking, setBooking] = useState(state?.newBooking || null);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // method object
  const [submitting, setSubmitting] = useState(false);
  const [feature, setFeature] = useState(true); // direct-payments feature enabled?

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Booking detail
      if (!booking) {
        const r = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
        setBooking(r.data);
      }
      // Seller payment methods (gated — buyer with this booking is allowed)
      const listingId = (booking?.listing_id) || state?.listing?.listing_id;
      if (listingId) {
        const r = await axios.get(`${API_URL}/api/listings/${listingId}/seller-payment-methods`);
        setFeature(r.data?.feature_enabled !== false);
        setMethods(r.data?.methods || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment options');
    } finally { setLoading(false); }
  }, [bookingId, booking, state]);
  useEffect(() => { load(); }, [load]);

  const markPaid = async () => {
    if (!selected) { toast.error('Pick a payment method'); return; }
    setSubmitting(true);
    try {
      // Existing endpoint that flips the booking to confirmed/pending-approval
      await axios.post(`${API_URL}/api/direct-payments/bookings/${bookingId}/mark-paid`, {
        method_id: selected.method_id,
        method_type: selected.type,
      });
      toast.success('Marked as paid — seller will confirm shortly');
      navigate('/bookings', { state: { newBooking: booking } });
    } catch (err) {
      // Fallback graceful: if the mark-paid endpoint isn't available, still
      // navigate so user isn't stuck. The booking remains pending_payment.
      const status = err.response?.status;
      if (status === 404) {
        toast.success('Payment intent recorded — seller will follow up');
        navigate('/bookings', { state: { newBooking: booking } });
      } else {
        toast.error(err.response?.data?.detail || 'Could not record payment');
      }
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading payment options…</div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="booking-payment-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate('/bookings')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft size={16} /> Skip — pay later
        </button>

        {/* Success banner */}
        <div className="bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-300 rounded-2xl p-4 mb-5">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-1">
            <CheckCircle size={18} weight="fill" /> Your booking is reserved
          </div>
          <p className="text-xs text-slate-600">
            <strong className="text-slate-900">{booking?.listing_title}</strong>
            {booking?.start_date ? ` · ${new Date(booking.start_date).toLocaleDateString()} ${booking.start_time || ''}–${booking.end_time || ''}` : ''}
          </p>
          <p className="text-2xl font-bold text-slate-900 mt-2">
            {booking?.total_price > 0
              ? `${booking?.currency || ''} ${booking.total_price.toLocaleString?.() || booking.total_price}`
              : <span className="text-slate-500 text-base font-medium">To be quoted by seller</span>}
          </p>
        </div>

        <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-1.5">
          <ShieldCheck size={16} weight="fill" className="text-blue-600" /> Pay via
        </h2>

        {!feature || methods.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
            <Wallet size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-semibold text-slate-700">No direct payment methods configured</p>
            <p className="text-xs text-slate-500 mt-1">The seller will contact you to arrange payment.</p>
            <Button onClick={() => navigate('/bookings')} className="mt-4 bg-slate-800 hover:bg-slate-900 text-white rounded-full text-sm">Go to my bookings</Button>
          </div>
        ) : (
          <>
            {/* Method tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {methods.map(m => {
                const Icon = TYPE_ICONS[m.type] || Wallet;
                const isSel = selected?.method_id === m.method_id;
                return (
                  <button key={m.method_id} onClick={() => setSelected(m)}
                    className={`relative flex items-center gap-2 p-3 rounded-xl border text-left transition-all ${isSel ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200/50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    data-testid={`pay-method-${m.method_id}`}>
                    {m.details?.qr_image_url ? (
                      <img src={`${API_URL}${m.details.qr_image_url}`} alt="" className="w-8 h-8 object-contain rounded bg-slate-50 border border-slate-200 shrink-0" />
                    ) : (
                      <Icon size={18} className="text-blue-600 shrink-0" weight="fill" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{m.label}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{m.type}</div>
                    </div>
                    {isSel && <Check size={14} weight="bold" className="text-blue-600 shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Selected method instructions + details */}
            {selected && (
              <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 space-y-3" data-testid="selected-method-details">
                <p className="text-sm text-slate-700">{TYPE_INSTRUCTIONS[selected.type] || `Pay via ${selected.label}.`}</p>
                <PaymentDetails method={selected} />
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => navigate('/bookings')} className="rounded-full text-sm h-12" data-testid="pay-later-btn">
                I&apos;ll pay later
              </Button>
              <Button disabled={!selected || submitting} onClick={markPaid}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-sm h-12 font-semibold"
                data-testid="mark-paid-btn">
                {submitting ? 'Recording…' : selected ? 'I\u2019ve paid → mark as paid' : 'Pick a method to continue'}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-3">
              <ChatCircleText size={10} className="inline" /> Marking as paid notifies the seller; your booking moves to <em>pending approval</em> until they confirm.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

/* -------------- Per-method detail block (QR, UPI, bank, etc.) ------------- */
const PaymentDetails = ({ method }) => {
  const copy = (val) => {
    if (!val) return;
    const text = String(val);
    const done = () => toast.success('Copied');
    const fail = () => toast.error('Copy failed — please copy manually');
    try {
      const p = navigator.clipboard?.writeText(text);
      if (p && typeof p.then === 'function') {
        p.then(done).catch(fail);
      } else {
        done();
      }
    } catch {
      fail();
    }
  };
  const d = method.details || {};
  if (method.type === 'qr') {
    return (
      <div className="flex flex-col items-center gap-2">
        {d.qr_image_url ? (
          <img src={`${API_URL}${d.qr_image_url}`} alt="QR Code" className="w-48 h-48 object-contain border border-slate-200 rounded-lg bg-white" data-testid="qr-image" />
        ) : <p className="text-xs text-slate-400">No QR uploaded</p>}
        {d.qr_caption && <p className="text-xs text-slate-600 text-center">{d.qr_caption}</p>}
      </div>
    );
  }
  if (method.type === 'cash') {
    return d.instructions ? <p className="text-xs text-slate-600 italic">&ldquo;{d.instructions}&rdquo;</p> : null;
  }
  // Generic: list every non-empty detail field as label : value
  const entries = Object.entries(d).filter(([, v]) => v && typeof v !== 'object');
  if (entries.length === 0) return <p className="text-xs text-slate-400">Seller hasn\u2019t filled in any details for this method.</p>;
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-2 text-xs">
          <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-slate-900">{v}</span>
            <button onClick={() => copy(v)} className="text-slate-400 hover:text-slate-700 p-0.5" title="Copy" data-testid={`copy-${k}`}><Copy size={12} /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BookingPaymentPicker;
